import { useEffect, useState } from "react";
import { MapPin, ArrowRight } from "lucide-react";

export default function Hero() {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100vh] bg-[color:var(--sr-black)] overflow-hidden"
      aria-label="Hero"
    >
      {/* Full-bleed background image, centered so the glowing sign is on screen */}
      <div className="absolute inset-0" data-testid="hero-image-wrap">
        <div
          className="absolute inset-0"
          style={{
            transform: `translateY(${offset * 0.12}px) scale(1.04)`,
            transition: "transform 100ms linear",
          }}
        >
          <img
            src="/images/hero-room.jpg"
            alt="Dark luxury Skin Refinery treatment room with a glowing floral mirror and treatment bed in Ozark, Missouri"
            className="w-full h-full object-cover hero-line"
            style={{
              objectPosition: "50% 45%",
              animationDelay: "200ms",
              animationDuration: "1600ms",
              animationTimingFunction: "cubic-bezier(0.22,1,0.36,1)",
              opacity: 0,
            }}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        {/* Cinematic vignette — heavy fade on the LEFT for text legibility, clear on the RIGHT so the glowing "Skin Refinery" sign stays bright and visible. */}
        <div className="absolute inset-y-0 left-0 w-[65%] pointer-events-none bg-gradient-to-r from-[#050505] via-[#050505]/85 to-transparent" />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#050505]/45 via-transparent to-transparent" />
      </div>

      {/* Text overlay */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 min-h-[100vh] flex items-center pt-32 pb-20 lg:pt-28">
        <div className="max-w-2xl">
          <h1
            className="font-serif text-[color:var(--sr-ivory)] leading-[0.98] text-5xl sm:text-6xl lg:text-7xl xl:text-[6rem] tracking-[-0.01em]"
            data-testid="hero-headline"
          >
            <span
              className="block hero-line"
              style={{ animationDelay: "300ms" }}
            >
              Refine Your Beauty.
            </span>
            <span
              className="block hero-line italic font-light text-[color:var(--sr-beige)]"
              style={{ animationDelay: "550ms", fontStyle: "italic" }}
            >
              Keep it you.
            </span>
          </h1>

          <div
            className="gold-line mt-8 hero-line"
            style={{ animationDelay: "800ms" }}
          />

          <p
            className="hero-line max-w-lg mt-8 text-[color:var(--sr-beige)] text-[15px] leading-relaxed"
            style={{ animationDelay: "950ms" }}
          >
            Results-driven skincare, Korean lash lifts, and brow artistry
            designed to refine your beauty while keeping you unmistakably you.
          </p>

          <div
            className="hero-line mt-10 flex flex-col sm:flex-row gap-4 sm:items-center"
            style={{ animationDelay: "1100ms" }}
          >
            <button
              onClick={() => scrollTo("book")}
              data-testid="hero-book-btn"
              className="btn-gold"
            >
              Book Your Experience <ArrowRight size={14} strokeWidth={1.5} />
            </button>
          </div>

          <div
            className="hero-line mt-8 flex items-center gap-2 text-[color:var(--sr-beige)] text-sm"
            style={{ animationDelay: "1250ms" }}
          >
            <MapPin
              size={14}
              strokeWidth={1.5}
              className="text-[color:var(--sr-gold)]"
            />
            <span className="tracking-wide">3897 N 20th St. Unit 1, Ozark, MO</span>
          </div>
        </div>
      </div>
    </section>
  );
}
